import { getIdToken } from 'firebase/auth';

import { auth } from 'external/firebase/index';

import { BASE_CLOUD_FUNCTION_URL } from './functionsBaseUrl';
import { formatErrorMessage } from './utils';

// Field names mirror the scraper service's JSON response verbatim.
/* eslint-disable camelcase */
// One real chat turn's outcome, once its SSE stream's `done` event lands —
// see streamChatMessage below. No `reply` field: the caller already has the
// full text, built up token by token via onToken as it streamed in.
export interface ChatStreamResult {
    session_id: string;
    conversation_id: number;
    is_new_conversation: boolean;
    // The stored assistant reply a thumbs up/down refers to; null for a turn
    // answered without the content service (a "pastaba" note).
    message_id: number | null;
    // Messages the student has left today; null for staff (no cap) and for a
    // turn that didn't count (a "pastaba" note), which leaves the shown count as is.
    remaining_today: number | null;
    daily_limit: number | null;
}

export interface DailyChatStats {
    date: string;
    user_messages: number;
    distinct_users: number;
}

// The course module a reply was grounded in (e.g. "JavaScript", "Python (2
// lygis)", "HTML ir CSS") — see cao-content-service's messages.module
// migration for why this is the raw per-lecture value, not a course-level
// grouping. Same date window as `daily`, most-asked-about first.
export interface TopicStats {
    topic: string;
    message_count: number;
}

export interface ChatStats {
    total_conversations: number;
    total_user_messages: number;
    distinct_users: number;
    messages_today: number;
    distinct_users_today: number;
    refusal_count: number;
    refusal_rate: number;
    daily: DailyChatStats[];
    top_topics: TopicStats[];
    rate_limited_users_today: number;
    positive_ratings: number;
    negative_ratings: number;
    web_answers: number;
    // Questions about a lesson the asker's group hasn't opened yet (in the window).
    locked_topic_count: number;
    // Every reply in the window — the "not covered" rate's denominator.
    assistant_messages: number;
}

export interface ChatHandoff {
    id: string;
    email: string | null;
    testAs: string[] | null;
    groupId: string | null;
    comment: string;
    summary: string | null;
    status: 'open' | 'resolved';
    environment: 'local' | 'production';
    createdAt: string | null;
    resolvedBy: string | null;
    resolvedAt: string | null;
}

export interface ChatTranscriptMessage {
    id: number;
    role: 'user' | 'assistant';
    content: string;
    created_at: string;
    rating: 1 | -1 | null;
}

export interface ChatGapTopic {
    topic: string;
    count: number;
    examples: string[];
}

export interface ChatGapReport {
    days: number;
    question_count: number;
    refused_count: number;
    negative_count: number;
    topics: ChatGapTopic[];
}

export interface ChatAuditEntry {
    id: string;
    viewerEmail: string | null;
    action: 'notes' | 'handoffs' | 'transcript' | 'gaps';
    handoffId?: string;
    studentUid?: string;
    days?: number;
    at: string | null;
}
/* eslint-enable camelcase */

export interface ChatNoteContext {
    question: string;
    answer: string;
}

export interface ChatNote {
    id: string;
    email: string | null;
    testAs: string[] | null;
    note: string;
    context: ChatNoteContext | null;
    environment: 'local' | 'production';
    createdAt: string | null;
}

// Must match the functions' CHAT_CONSENT_VERSION; bump both whenever the
// consent text (chat_intro_body_*, chat_consent_label) changes.
export const CHAT_CONSENT_VERSION = '2026-10-29';

// A message starting with the word "pastaba" is filed as a note (see the
// functions' chatNotes.ts, which decides for real).
export const isChatNote = (message: string): boolean => /^\s*pastaba(?![\p{L}\p{N}])/iu.test(message);

// Splits an SSE byte stream on the blank-line `\n\n` event separator,
// tolerating chunk boundaries that land mid-event (fetch's reader hands back
// arbitrary byte chunks, not one per event) by keeping whatever's left over
// in `buffer` between calls.
const extractSseEvents = (buffer: string): { events: Record<string, unknown>[]; rest: string } => {
    const events: Record<string, unknown>[] = [];
    let rest = buffer;
    let separatorIndex = rest.indexOf('\n\n');
    while (separatorIndex !== -1) {
        const rawEvent = rest.slice(0, separatorIndex).trim();
        rest = rest.slice(separatorIndex + 2);
        if (rawEvent.startsWith('data: ')) {
            try {
                events.push(JSON.parse(rawEvent.slice('data: '.length)));
            } catch {
                // Malformed frame — drop it rather than throw mid-stream.
            }
        }
        separatorIndex = rest.indexOf('\n\n');
    }
    return { events, rest };
};

export const CHAT_STATUS_SEARCHING_DOCS = 'searching_docs';

// Sends one chat turn to the `chat` Cloud Function as Server-Sent Events, so the
// reply can be animated onto the screen token by token (like kursuok.lt) instead
// of appearing all at once. `sessionId` is omitted on the very first turn; the
// final `done` event carries the server-assigned `session_id` to reuse on
// subsequent turns. `onToken` fires once per text delta, in order; the promise
// resolves only once the stream's `done` event has landed (or rejects/returns
// `ok: false` on any failure, including one signaled in-band mid-stream — see
// the Cloud Function proxy's own comment on why that has to be in-band here).
// `testAs` is for super-admins only: the chat function treats one as a
// synthetic user with that `group` array (see ChatTestAsBar). `noteContext`
// rides along only with a "pastaba" note — the exchange the note is about.
// `onStatus` reports what the turn is doing while no text arrives (today only
// CHAT_STATUS_SEARCHING_DOCS, sent before the official-documentation block).
// `language` is the UI language ("lt"/"en"): the reply is always written in it.
export const streamChatMessage = async (
    message: string,
    sessionId: string | undefined,
    onToken: (delta: string) => void,
    testAs?: string[] | null,
    noteContext?: ChatNoteContext | null,
    onStatus?: (status: string) => void,
    language?: string,
): Promise<ChatStreamResult | { ok: false; code: string }> => {
    try {
        if (!auth.currentUser) {
            throw new Error('auth/no-user-found');
        }
        const token = await getIdToken(auth.currentUser);
        const response = await fetch(`${BASE_CLOUD_FUNCTION_URL}/chat`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
                session_id: sessionId,
                message,
                ...(testAs ? { test_as: { group: testAs } } : {}),
                ...(noteContext ? { note_context: noteContext } : {}),
                ...(language ? { language } : {}),
            }),
        });

        if (!response.ok) {
            // Only a pre-stream failure (auth, validation, rate limit, pilot
            // gating) reaches here as plain JSON — see the Cloud Function
            // proxy: once its SSE response has started, every failure is an
            // in-band event further down, never a non-2xx status.
            const data = await response.json().catch(() => null);
            if (data?.error) {
                throw new Error(data.error);
            }
            if (response.status === 403) {
                throw new Error('auth/forbidden-access');
            }
            throw new Error(`chat/failed-${response.status}`);
        }
        if (!response.body) {
            throw new Error('chat/upstream-error');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let result: ChatStreamResult | null = null;
        let inBandError: string | null = null;

        const handleEvent = (event: Record<string, unknown>): void => {
            if (typeof event.token === 'string') {
                onToken(event.token);
            } else if (typeof event.status === 'string') {
                onStatus?.(event.status);
            } else if (typeof event.error === 'string') {
                inBandError = event.error;
            } else if (event.done === true) {
                result = {
                    session_id: event.session_id as string,
                    conversation_id: event.conversation_id as number,
                    is_new_conversation: event.is_new_conversation as boolean,
                    message_id: typeof event.message_id === 'number' ? event.message_id : null,
                    remaining_today: typeof event.remaining_today === 'number' ? event.remaining_today : null,
                    daily_limit: typeof event.daily_limit === 'number' ? event.daily_limit : null,
                };
            }
        };

        for (;;) {
            // eslint-disable-next-line no-await-in-loop
            const { value, done } = await reader.read();
            if (done) {
                break;
            }
            buffer += decoder.decode(value, { stream: true });
            const { events, rest } = extractSseEvents(buffer);
            buffer = rest;
            events.forEach(handleEvent);
        }

        if (inBandError) {
            throw new Error(inBandError);
        }
        if (!result) {
            throw new Error('chat/upstream-error');
        }
        return result;
    } catch (error: any) {
        if (error?.message?.includes('CORS') || error?.message?.includes('Failed to fetch')) {
            return { ok: false, code: 'something_went_wrong' };
        }
        return formatErrorMessage(error);
    }
};

// Fetches the chat observability numbers for the super-admin insights page.
// The `chat` Cloud Function's `/stats` route is gated by authorizeSuperAdmin
// server-side — this call fails with a 403 for anyone else, same as any other
// super-admin-only endpoint in this app.
export const fetchChatStats = async (days: number): Promise<ChatStats | { ok: false; code: string }> => {
    try {
        if (!auth.currentUser) {
            throw new Error('auth/no-user-found');
        }
        const token = await getIdToken(auth.currentUser);
        const response = await fetch(`${BASE_CLOUD_FUNCTION_URL}/chat/stats?days=${days}`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json();
        if (!response.ok) {
            if (response.status === 403) {
                throw new Error('auth/forbidden-access');
            }
            throw new Error(data?.error || `chat/failed-${response.status}`);
        }
        return data as ChatStats;
    } catch (error: any) {
        if (error?.message?.includes('CORS') || error?.message?.includes('Failed to fetch')) {
            return { ok: false, code: 'something_went_wrong' };
        }
        return formatErrorMessage(error);
    }
};

export interface ChatTestPersonaGroup {
    id: string;
    name: string;
    courseCount: number;
    unlockedModuleCount: number;
}

// Emulator-only route (codeacademy-online-functions chat.ts) — 404s anywhere else.
export const fetchChatTestPersonas = async (): Promise<ChatTestPersonaGroup[]> => {
    if (!auth.currentUser) {
        return [];
    }
    const token = await getIdToken(auth.currentUser);
    const response = await fetch(`${BASE_CLOUD_FUNCTION_URL}/chat/test-personas`, {
        headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
        return [];
    }
    const data = await response.json();
    return Array.isArray(data?.groups) ? data.groups : [];
};

export const recordChatConsentOnServer = async (): Promise<boolean> => {
    try {
        if (!auth.currentUser) {
            return false;
        }
        const token = await getIdToken(auth.currentUser);
        const response = await fetch(`${BASE_CLOUD_FUNCTION_URL}/chat/consent`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ version: CHAT_CONSENT_VERSION }),
        });
        return response.ok;
    } catch {
        return false;
    }
};

// Super-admin only, like /chat/stats.
export const fetchChatNotes = async (): Promise<ChatNote[] | { ok: false; code: string }> => {
    try {
        if (!auth.currentUser) {
            throw new Error('auth/no-user-found');
        }
        const token = await getIdToken(auth.currentUser);
        const response = await fetch(`${BASE_CLOUD_FUNCTION_URL}/chat/notes`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json();
        if (!response.ok) {
            if (response.status === 403) {
                throw new Error('auth/forbidden-access');
            }
            throw new Error(data?.error || `chat/failed-${response.status}`);
        }
        return Array.isArray(data?.notes) ? data.notes : [];
    } catch (error: any) {
        if (error?.message?.includes('CORS') || error?.message?.includes('Failed to fetch')) {
            return { ok: false, code: 'something_went_wrong' };
        }
        return formatErrorMessage(error);
    }
};

// One authenticated JSON call to the `chat` Cloud Function. Resolves to the
// parsed body, or `{ ok: false, code }` the same way the other calls here do.
const callChatFunction = async <T>(
    path: string,
    init: { method?: string; body?: unknown } = {},
): Promise<T | { ok: false; code: string }> => {
    try {
        if (!auth.currentUser) {
            throw new Error('auth/no-user-found');
        }
        const token = await getIdToken(auth.currentUser);
        const response = await fetch(`${BASE_CLOUD_FUNCTION_URL}/chat${path}`, {
            method: init.method ?? 'GET',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            ...(init.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
        });
        const data = response.status === 204 ? {} : await response.json().catch(() => ({}));
        if (!response.ok) {
            if (response.status === 403 && !data?.error) {
                throw new Error('auth/forbidden-access');
            }
            throw new Error(data?.error || `chat/failed-${response.status}`);
        }
        return data as T;
    } catch (error: any) {
        if (error?.message?.includes('CORS') || error?.message?.includes('Failed to fetch')) {
            return { ok: false, code: 'something_went_wrong' };
        }
        return formatErrorMessage(error);
    }
};

export const isChatCallError = (value: unknown): value is { ok: false; code: string } =>
    typeof value === 'object' && value !== null && (value as { ok?: unknown }).ok === false;

const testAsBody = (testAs?: string[] | null) => (testAs ? { test_as: { group: testAs } } : {});

export const rateChatMessage = (messageId: number, rating: 1 | -1 | null, testAs?: string[] | null) =>
    callChatFunction<Record<string, never>>('/rating', {
        method: 'POST',
        body: { message_id: messageId, rating, ...testAsBody(testAs) },
    });

export const requestChatHandoff = (sessionId: string, comment: string, testAs?: string[] | null) =>
    callChatFunction<{ ok: true }>('/handoff', {
        method: 'POST',
        body: { session_id: sessionId, comment, ...testAsBody(testAs) },
    });

export const fetchChatHandoffs = () => callChatFunction<{ handoffs: ChatHandoff[] }>('/handoffs');

export const setChatHandoffStatus = (id: string, status: ChatHandoff['status']) =>
    callChatFunction<{ ok: true }>(`/handoffs/${encodeURIComponent(id)}/status`, {
        method: 'POST',
        body: { status },
    });

export const fetchChatHandoffTranscript = (id: string) =>
    callChatFunction<{ messages: ChatTranscriptMessage[]; expired: boolean }>(
        `/handoffs/${encodeURIComponent(id)}/transcript`,
    );

export const fetchChatGaps = (days: number) => callChatFunction<ChatGapReport>(`/gaps?days=${days}`);

export const fetchChatAudit = () => callChatFunction<{ entries: ChatAuditEntry[] }>('/audit');

// What the panel loads before any question: the lessons it can list and,
// for a student, today's message allowance (null for staff).
/* eslint-disable camelcase */
export interface ChatPrefetch {
    lessons: ChatScopeLesson[];
    daily_limit?: number;
    remaining_today?: number | null;
}
/* eslint-enable camelcase */

export interface ChatScopeLesson {
    module: string;
    lesson: string;
}

/* eslint-disable camelcase */
// A chat as the server keeps it (30 days): enough to list it before opening.
export interface ChatServerSession {
    session_id: string;
    title: string;
    created_at: string;
    updated_at: string;
    message_count: number;
}

export interface ChatServerMessage {
    role: 'user' | 'assistant';
    content: string;
    message_id?: number;
    rating?: 1 | -1 | null;
}
/* eslint-enable camelcase */

export const fetchChatSessions = (testAs?: string[] | null) =>
    callChatFunction<{ sessions: ChatServerSession[] }>('/sessions', { method: 'POST', body: testAsBody(testAs) });

export const fetchChatSession = (sessionId: string, language: string, testAs?: string[] | null) =>
    callChatFunction<{ messages: ChatServerMessage[] }>(`/sessions/${encodeURIComponent(sessionId)}`, {
        method: 'POST',
        body: { ...testAsBody(testAs), language },
    });

export const deleteChatSessionsOnServer = (sessionIds: string[], testAs?: string[] | null) =>
    callChatFunction<Record<string, never>>('/sessions-delete', {
        method: 'POST',
        body: { ...testAsBody(testAs), session_ids: sessionIds },
    });

// Called as the dashboard loads: the function caches the caller's entitlement
// and wakes the content service, so the first real question skips both.
export const prefetchChatScope = (testAs?: string[] | null) =>
    callChatFunction<ChatPrefetch>('/prefetch', {
        method: 'POST',
        body: testAs ? { test_as: { group: testAs } } : {},
    });
